import { useMemo, useState } from 'react'

import { useSession } from '@/features/auth/use-session'
import {
  PARAM,
  usePortfolioFilters,
  type PortfolioScope,
} from '@/features/portfolio/use-portfolio-filters'
import { usePortfolioProjects } from '@/features/portfolio/api'
import { PortfolioFiltersBar } from '@/features/portfolio/components/PortfolioFilters'
import { PageHeader } from '@/shared/components/common'
import { EmptyState, ErrorState, NoResultsState, SkeletonRows } from '@/shared/components/data'
import { Segmented } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { calendarDay } from '@/shared/lib/due-date'

import { TimelineChart } from './components/TimelineChart'
import { groupProjects, timelineDomain, type GroupBy, type TimelineWindow } from './domain'

/**
 * `/timeline` — el portafolio sobre un eje de tiempo.
 *
 * Es la cuarta forma de mirar la MISMA lista: el portafolio dice qué hay,
 * prioridades en qué orden, el tablero en qué fase, y esto **cuándo**. Por eso
 * comparte la barra de filtros entera en vez de tener la suya: "lo de Nube
 * Andina" tiene que significar lo mismo en las cuatro.
 *
 * Los cuatro cortes que pide un manager salen de un solo control: agrupando por
 * empresa se ve cómo viene cada cuenta, por responsable cómo viene cada
 * persona, y sin agrupar queda el total sobre un eje — que es la vista de
 * "cómo cuadra todo". El cuarto, un proyecto puntual, es filtrar y ver su
 * barra sola.
 */
export function TimelinePage() {
  const t = useT()
  const controls = usePortfolioFilters()
  const { filters, queryFilters, isFiltered, clearFilters, setFilter } = controls
  const { me } = useSession()

  const scope: PortfolioScope = filters.scope ?? (me?.role === 'tech_member' ? 'mio' : 'todo')

  const scopedFilters = useMemo(
    () => (scope === 'mio' && me ? { ...queryFilters, [PARAM.owner]: [me.id] } : queryFilters),
    [scope, me, queryFilters],
  )

  /*
    La línea de tiempo pide los culminados SIEMPRE. No es un detalle: "qué
    entregamos este trimestre" es una de las preguntas que esta pantalla existe
    para contestar, y el default del endpoint es no traerlos — el mismo default
    que dejó la columna "Culminado" del tablero vacía durante meses.
  */
  const projects = usePortfolioProjects(scopedFilters, { needsDone: true })

  // `calendarDay()` es la MISMA fuente de "hoy" que los badges de vencimiento y
  // que el recorte de Culminado. Se congela por render para que la línea de hoy
  // y las barras no puedan calcularse contra dos días distintos.
  const [today] = useState(() => calendarDay())

  const domain = useMemo(() => timelineDomain(filters.window, today), [filters.window, today])

  const groups = useMemo(
    () =>
      groupProjects(
        projects.data ?? [],
        filters.group,
        t('common.label.noOwner'),
        t('timeline.allProjects'),
      ),
    [projects.data, filters.group, t],
  )

  const groupOptions = [
    { value: 'empresa', label: t('timeline.groupBy.company') },
    { value: 'responsable', label: t('timeline.groupBy.owner') },
    { value: 'ninguno', label: t('timeline.groupBy.none') },
  ] as const

  const windowOptions = [
    { value: 'trimestre', label: t('timeline.window.quarter') },
    { value: 'semestre', label: t('timeline.window.half') },
    { value: 'anio', label: t('timeline.window.year') },
  ] as const

  const items = projects.data ?? []

  return (
    <div className="flex flex-col">
      <PageHeader title={t('timeline.title')} description={t('timeline.description')} />

      <PortfolioFiltersBar {...controls} scope={scope} showView={false} />

      <div className="flex flex-wrap items-center gap-2 px-4 pt-3">
        <Segmented
          label={t('timeline.groupByLabel')}
          value={filters.group}
          options={groupOptions}
          onChange={(value: GroupBy) => setFilter('group', value)}
        />
        <Segmented
          label={t('timeline.windowLabel')}
          value={filters.window}
          options={windowOptions}
          onChange={(value: TimelineWindow) => setFilter('window', value)}
        />
      </div>

      <div className="flex flex-col gap-4 p-4">
        {projects.isPending ? (
          <SkeletonRows rows={10} />
        ) : projects.isError ? (
          <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />
        ) : items.length === 0 && isFiltered ? (
          <NoResultsState onClearFilters={clearFilters} />
        ) : items.length === 0 ? (
          <EmptyState
            title={t('timeline.empty.title')}
            description={t('timeline.empty.description')}
          />
        ) : (
          <TimelineChart groups={groups} domain={domain} today={today} groupBy={filters.group} />
        )}
      </div>
    </div>
  )
}
